import { useEffect, useState } from "react";
import styles from "./index.module.css";
import {
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import {
  IBooking,
  BookingStatus,
  PDFViewMode,
  ICreditNote,
  IConfirmationDialogData,
  BookingType,
  IIdentityConfiguration,
} from "../../../../reducers/bookings/types";
import {
  formatGraphQLErrorMessage,
} from "../../../utils";
import { GET_BOOKING } from "../../../../graphql/bookings/getBookingQuery";
import { DateTime as d, Settings } from "luxon";
import Quote from "./Quote";
import PaymentDialog from "../../../BookingConfirmation";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import PdfViewer from "../../../Pdf/ShowPdf";
import BookingSummary from "./BookingManagement";
import DownloadPdf from "../../../Pdf/DownloadPdf";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../store";
import { getLocalizedBookingSyntex, getLocalizedCancelBookingSyntex, getLocalizedRASyntex } from "../../../../utils/localized.syntex";
import { BusinessCustomerStatus, CustomerType, CustomerVerificationStatus, IUser, UserRole } from "../../../../reducers/auth/types";
import { GET_USER } from "../../../../graphql/user/getUser";
import { VerificationDialog } from "../../../VerificationDialog/VerificationDialog";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@material-ui/core";
import { CANCEL_BOOKING } from "../../../../graphql/bookings/cancelBookingMutation";
import { GET_ADJUSTABLE_CREDIT_NOTES_FOR_BOOKING } from "../../../../graphql/bookings/getAdjustableCreditNotesForBooking";
import BusinessCustomerVerificationDialog from "../../../WarningDialogBox/BusinessCustomerVerificationDialog";
import { defaultConfirmationDialogData } from "../../Summary/const";
import { CONFIRM_BOOKING } from "../../../../graphql/bookings/confirmBookingMutation";
import { Alert } from "@material-ui/lab";
import { socket } from "../../../../utils/socket";
import { SOCKET_EVENTS } from "../../utils";
import { GET_ALL_DOCUMENT_TYPES } from "../../../../graphql/customers/getAllDocumentTypesQuery";
import { IDocumentType } from "../../../Profile";
import { bookingCategories } from "../../NewBooking/utils";
import { BOOKING_CATEGORY, IdentityVerificationStatus } from "../../../../reducers/website/types";
import CloseIcon from "@material-ui/icons/Close";
import { IdentityVerificationCard } from "../../../IdentityVerification/IdentityVerificationCard";
import { hasIdvCredits, isIdvStatusPassed } from "../../../IdentityVerification/utils";
import { GET_IDV_CONFIGURATION } from "../../../../graphql/user/getIdvConfigurationQuery";
import { useTranslation } from "react-i18next";
import { ExtendBookingDialog } from "./ExtendBookingDialog";
export interface IDamageList {
  number: number;
  damageKind: string;
  image: string;
}

const BookingManagement = () => {
  const history = useHistory();
  const snackbar = useSnackBar();
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const authUser = useSelector((state: IAppState) => state.authReducer.authUser);
  const { country } = website.organisation.address;
  const { id: bookingId } = useParams<{ id: string }>();
  const searchParams = useLocation();
  const [booking, setBooking] = useState<IBooking>();
  const [paymentDialogOpen, setPaymentDialogOpen] = useState<boolean>(false);
  const [pdfId, setPdfId] = useState<string | undefined>("");
  const [pdfViewMode, setPdfViewMode] = useState<string>();
  const [pdfFileName, setPdfFileName] = useState<string>("");
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [pdfTitle, setPdfTitle] = useState<string>("");
  const [pdfOpen, setPdfOpen] = useState<boolean>(false);
  const [raLoading, setRALoading] = useState<boolean>(false);
  const [adjustableCreditNotes, setAdjustableCreditNotes] = useState<ICreditNote[]>([]);
  const [adjustableAmount, setAdjustableAmount] = useState<number>(0);
  const [confirmationPrompt, setConfirmationPropmt] = useState<boolean>(false);
  const [openVerificationDialog, setOpenVerificationDialog] = useState(false);
  const [user, setUser] = useState<IUser>();
  const [openIdentityVerificationDialog, setOpenIdentityVerificationDialog] = useState(false);
  const [idvConfiguration, setIdvConfiguration] = useState<IIdentityConfiguration | null>();
  // The user's identityVerification.status isn't refreshed by
  // IdentityVerificationCard's own polling - this tracks the live result.
  const [identityVerificationStatusOverride, setIdentityVerificationStatusOverride] = useState<string>();
  // Set when a non-mandatory verification is skipped so Pay & Confirm doesn't
  // keep reopening the dialog.
  const [identityVerificationSkipped, setIdentityVerificationSkipped] = useState(false);
  const [dialogBoxOpen, setDialogBoxOpen] = useState(false);
  const [cancellationDialogOpen, setCancellationDialogOpen] =
    useState<boolean>(false);
  const [extendBookingDialogOpen, setExtendBookingDialogOpen] =
    useState<boolean>(false);
  const [rentalAgreementUrl, setRentalAgreementUrl] = useState<string>();
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [confirmationDialogData, setConfirmationDialogData] = useState<
    IConfirmationDialogData
  >(defaultConfirmationDialogData);
  let CANCELLATION_REASONS = [
    'Requirement changed',
    'Other'
  ];
  const [showCancelError, setCancelError] = useState({ display: false, message: "" });
  const [allRequiredDocument, setAllRequiredDocument] = useState<IDocumentType[]>([]);
  const isContractHire = website.organisation.bookingCategories.some((category) => category.isContractHire === true)
  const { t } = useTranslation();

  if (!(website.organisation.closeGroupSharingEnabled || (website.organisation.enableBookingCreationForApprovedDriver && user?.role == UserRole.DRIVER))) {
    CANCELLATION_REASONS = [...CANCELLATION_REASONS, "Price"]
  }
  else {
    CANCELLATION_REASONS = [...CANCELLATION_REASONS]
  }

  const [getBooking, { loading: bookingLoadingData, data: bookingData }] =
    useLazyQuery(GET_BOOKING, {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (!data.consumerGetBooking) {
          history.push("/account/bookings");
        }
      },
      onError: (error: ApolloError) => {
        snackbar({
          message: formatGraphQLErrorMessage(error.message),
          variant: SnackBarVariant.ERROR
        });
      }
    });

  const [getAllDocumentTypes, { data: allDocumentTypesData, loading: documentTypesLoading }] = useLazyQuery(GET_ALL_DOCUMENT_TYPES);

  const [confirmBookingMutation] = useMutation(CONFIRM_BOOKING, {
    onCompleted: ({ consumerConfirmBooking }) => {
      history.push(`/booking?id=${consumerConfirmBooking.id}`);
    },
    onError: (error: ApolloError) =>
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      }),
  });

  const [cancelBookingMutation, { loading: cancelBookingLoading }] = useMutation(CANCEL_BOOKING, {
    onCompleted: ({ consumerCancelBooking }) => {
      if (showCancelError.display) {
        clearCancelError();
      }
      snackbar({
        message: `${getLocalizedBookingSyntex(country)} ${t("snackBar:cancelledSuccessfully")}`,
        variant: SnackBarVariant.SUCCESS
      });
      setCancellationDialogOpen(false)
      setConfirmationDialogData({
        ...confirmationDialogData,
        isOpen: false
      });
      setCancellationDialogOpen(false)
      getBooking({
        variables: {
          bookingId: consumerCancelBooking.id
        }
      });
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
      console.log(error)
      history.push("/account/bookings");
    }
  });

  const [getUser] = useLazyQuery(GET_USER, {
    onCompleted: ({ user }: { user: IUser }) => {
      if (user) {
        setUser(user)
      }
    },
    fetchPolicy: 'no-cache',
  });

  const [loadIdvConfiguration] = useLazyQuery(GET_IDV_CONFIGURATION, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data.getIdvConfiguration) {
        setIdvConfiguration(data.getIdvConfiguration);
      }
    }
  });

  useEffect(() => {
    if (website.tenancy?.identityVerification?.id && user) {
      loadIdvConfiguration({
        variables: {
          configurationId: website.tenancy.identityVerification.id
        }
      });
    }
  }, [website.tenancy?.identityVerification?.id, user]);

  const [getAdjustableCreditNotesForBooking, { data: adjustableCreditNotesData }] = useLazyQuery(
    GET_ADJUSTABLE_CREDIT_NOTES_FOR_BOOKING, {
    fetchPolicy: "network-only"
  })

  const handleReasonChange = (reason: string) => {
    clearCancelError();
    setSelectedReason(reason);
    if (reason !== 'Other') {
      setOtherReason('');
    }
  };

  const isReasonValid = () => {
    if (selectedReason === 'Other') {
      return otherReason.trim() !== '';
    }
    return selectedReason !== '';
  };

  const handleSubmit = () => {
    if (isReasonValid() && booking) {
      cancelBookingMutation({
        variables: {
          bookingId: booking.id,
          branchId: booking.branchId,
          cancellationReason: selectedReason === "Other" ? otherReason : selectedReason
        }
      })
    } else {
      if (selectedReason === 'Other') {
        setCancelError({
          message: t("snackBar:selectReasonForCancel"),
          display: true
        });
      }
      else {
        setCancelError({
          message: t("snackBar:selectReasonForCancel"),
          display: true
        });
      }
    }
  };

  useEffect(() => {
    if (authUser) {
      getUser({
        variables: {
          id: authUser._id,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (bookingId) {
      getBooking({
        variables: {
          bookingId,
        },
      });
      getAdjustableCreditNotesForBooking({
        variables: {
          bookingId
        }
      })
    }
  }, []);

  useEffect(() => {
    socket.auth = {
      userId: authUser?._id,
    };
    socket.connect();
    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    socket.on(SOCKET_EVENTS.GET_UPDATED_BOOKING, (data) => {
      getBooking({
        variables: {
          bookingId: data.bookingId
        }
      })
    });

    socket.on(SOCKET_EVENTS.RENTAL_AGGREMENT_GENERATED, (data) => {
      setRentalAgreementUrl(data.rentalAgreementUrl);
    });

    return () => {
      socket.off(SOCKET_EVENTS.GET_UPDATED_BOOKING);
      socket.off(SOCKET_EVENTS.RENTAL_AGGREMENT_GENERATED);
    }
  }, []);

  const calcAdjustableAmount = (creditNotes: ICreditNote[]) => {
    let adjustableAmount: number = 0;
    if (creditNotes.length) {
      creditNotes.forEach((note: ICreditNote) => {
        adjustableAmount += note.availableAmount
      })
    };
    return adjustableAmount;
  }

  useEffect(() => {
    if (adjustableCreditNotesData && adjustableCreditNotesData.adjustableCreditNotesForBooking?.length) {
      setAdjustableCreditNotes(adjustableCreditNotesData.adjustableCreditNotesForBooking)
      const amount = calcAdjustableAmount(adjustableCreditNotesData.adjustableCreditNotesForBooking);
      setAdjustableAmount(amount)
    }
  }, [adjustableCreditNotesData])

  useEffect(() => {
    if (bookingData && bookingData.consumerGetBooking) {
      const _booking = bookingData.consumerGetBooking;
      socket.emit(SOCKET_EVENTS.REQUEST_VIEW_BOOKING, { bookingId: _booking.id });
      setConfirmationPropmt(false);
      if (_booking.rentalAgreementUrl) {
        setRentalAgreementUrl(
          _booking.rentalAgreementUrl
        );
      }
      setBooking(_booking);
      Settings.defaultZone = _booking.branch?.timeZone || "Europe/London"
    }
    return () => {
      Settings.defaultZone = "Europe/London"
    }
  }, [bookingData]);

  const userType = user?.businesscustomers ? CustomerType.BUSINESS : CustomerType.INDIVIDUAL;
  useEffect(() => {
    if (allDocumentTypesData && allDocumentTypesData.getAllDocumentTypes) {
      const requiredDocuments = allDocumentTypesData.getAllDocumentTypes.filter((e: any) => {
        return e.isMandatory && e.applicableFor && e.applicableFor.length && e.applicableFor.includes(userType);
      });
      setAllRequiredDocument(requiredDocuments);
    }
  }, [allDocumentTypesData, userType]);

  if (bookingLoadingData && (!booking || booking.status === BookingStatus.QUOTE)) {
    return <CircularProgress size={20} thickness={5} />;
  }

  if (
    (!booking && confirmationPrompt) ||
    (booking && booking.status === BookingStatus.CONFIRMATION_IN_PROCESS) ||
    (booking &&
      booking.status === BookingStatus.QUOTE &&
      searchParams &&
      searchParams.search.includes("redirect_status=succeeded")
    )
  ) {
    return (
      <Grid container alignItems="center" spacing={1}>
        <Grid item>
          <CircularProgress size={20} thickness={5} />
        </Grid>
        <Grid item>
          <Typography variant="h4" className="padding-right">
            {t("root:bookingConfirmationInProgress")}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  if (!booking) {
    return <></>
  }

  const canCancel = () => {
    if (booking) {
      return (
        booking.status === BookingStatus.QUOTE ||
        booking.status === BookingStatus.CONFIRMED
      );
    }
    return false;
  };

  const clearCancelError = () => {
    setCancelError({ message: "", display: false });
  }

  const hideUpdateButton =
    user?.businesscustomers &&
    user.businesscustomers?.portalBookingDisabled;


  const isApprovedDriverSkippingPayment = !!(
    (user?.businesscustomers && user.businesscustomers.creditLimitEnabled && user.role === UserRole.ADMIN_USER) ||
    (website.organisation.enableBookingCreationForApprovedDriver &&
      user?.role == UserRole.DRIVER &&
      user?.businesscustomers?.creditLimitEnabled &&
      user?.businesscustomers?.enableSkipPaymentForApprovedDrivers
    ));

  const isIdentityVerificationRequired =
    Boolean(website.tenancy?.identityVerification?.enabled) &&
    Boolean(idvConfiguration?.verificationPoints?.bookingCreation?.enabled) &&
    hasIdvCredits(idvConfiguration) &&
    Boolean(user?.customer) &&
    !isIdvStatusPassed(user?.customer?.identityVerification?.status) &&
    !isIdvStatusPassed(identityVerificationStatusOverride) &&
    !identityVerificationSkipped;

  const isIdentityVerificationMandatory = Boolean(idvConfiguration?.verificationPoints?.bookingCreation?.required);

  const handlePayAndConfirm = (skipIdentityVerificationCheck = false) => {
    if (!skipIdentityVerificationCheck && isIdentityVerificationRequired) {
      setOpenIdentityVerificationDialog(true);
      return;
    }
    getAllDocumentTypes()
    if (user?.customer) {
      if (website.organisation.requiresCustomerVerification &&
        user?.customer?.status === CustomerVerificationStatus.IN_REVIEW) {
        return snackbar({
          message: t("snackBar:verificationStatusInReview"),
          variant: SnackBarVariant.ERROR
        });
      }
      if (website.organisation.requiresCustomerVerification && (
        user?.customer?.status === CustomerVerificationStatus.PENDING ||
        user?.customer?.status === CustomerVerificationStatus.DECLINED
      )) {
        setOpenVerificationDialog(true);
      } else if ((!website.organisation.requiresCustomerVerification && d.fromISO(booking.pickupDateTime) > d.now()) ||
        (website.organisation.requiresCustomerVerification && user?.customer?.status === CustomerVerificationStatus.APPROVED
          && d.fromISO(booking.pickupDateTime) > d.now())) {
        setPaymentDialogOpen(true)
      }
    }
    if (user?.businesscustomers && d.fromISO(booking.pickupDateTime) > d.now()) {
      if ((website.enableBusinessCustomerSignup && website.organisation.requiresCustomerVerification && (
        user.businesscustomers.status === BusinessCustomerStatus.PENDING ||
        user.businesscustomers.status === BusinessCustomerStatus.DECLINED))) {
        setDialogBoxOpen(true);
        return snackbar({
          message: t("snackBar:verificationStatusPending"),
          variant: SnackBarVariant.ERROR
        })
      } else {
        setPaymentDialogOpen(true)
      }
    }
  };

  return (
    <div className={styles.column}>
      {paymentDialogOpen && (
        <PaymentDialog
          open={paymentDialogOpen}
          setOpen={(open: boolean) => setPaymentDialogOpen(open)}
          booking={booking}
          onConfirmation={(bookingId) => {
            setPaymentDialogOpen(false);
            getBooking({
              variables: {
                bookingId
              },
            });
          }
          }
          returnUrl={`account/bookings/${booking.id}`}
          adjustableCreditNotes={adjustableCreditNotes}
        />
      )}
      {pdfOpen ? (
        <div>
          <PdfViewer
            open={pdfOpen}
            document={pdfId}
            fileName={pdfFileName}
            title={pdfTitle}
            raLoading={raLoading}
            handleClose={() => {
              setPdfOpen(false);
            }}
            handleMail={() => { }}
            booking={booking}
            sendEmail={sendEmail}
            {...(pdfViewMode === PDFViewMode.VC_REPORT && {
              renderMode: "svg",
            })}
          />
        </div>
      ) : (
        ""
      )}

      {cancellationDialogOpen && (
        <Dialog
          open={cancellationDialogOpen}
          onClose={() => {
            setSelectedReason("");
            clearCancelError();
            setCancellationDialogOpen(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{getLocalizedCancelBookingSyntex(country)}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description"> {t("root:reasonToCancelTheBooking")}: </DialogContentText>
            <RadioGroup value={selectedReason} onChange={(e) => handleReasonChange(e.target.value)}>
              {CANCELLATION_REASONS.map((reason) => (
                <FormControlLabel key={reason} value={reason} control={<Radio />} label={reason} />
              ))}
            </RadioGroup>

            {selectedReason === 'Other' && (
              <div>
                <h5 style={{ marginBottom: '0.25rem' }}>{t("root:pleaseProvideTheReason")}:</h5>
                <TextField
                  type="text"
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    style: {
                      padding: '10px 14px',
                    },
                  }}
                />
              </div>
            )}
            {showCancelError.display &&
              <Alert severity="error">{showCancelError.message}</Alert>
            }
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="primary" onClick={handleSubmit} disabled={cancelBookingLoading}  >
              {(cancelBookingLoading) && <CircularProgress size={14} style={{ color: "white", marginRight: "10px" }} />}
              {t("root:confirm")}
            </Button>

            <Button variant="outlined" color="secondary" onClick={() => (setCancellationDialogOpen(false))} disabled={cancelBookingLoading} >
              {t("misc:cancel")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <div className={styles.header}>
        <Typography
          variant="h4"
          className="semi-bold flex cross-center space-between"
        >
          <IconButton
            size="small"
            edge="start"
            onClick={() => history.push(`/account/bookings`)}
          >
            <ArrowBackIcon />
          </IconButton>
          <span className="margin-left">
            {booking.status === BookingStatus.QUOTE ? booking.isSubscription ? "" : t("root:quote") : `${booking.isSubscription ? (isContractHire ? t("root:contractHire") : t("root:subscription")) : booking.isFleetAllocation ? t('root:fleetAllocation') : getLocalizedBookingSyntex(country)} ${t("root:management")}`}
          </span>
        </Typography>

        {bookingLoadingData &&
          <div className={styles.updatesLoader}>
            <CircularProgress size={15} />
          </div>
        }

        <div className={styles.headerButtons}>
          {booking.status === BookingStatus.REQUIRES_APPROVAL && (
            <>
              <Button variant="outlined" color="secondary" onClick={() => (setCancellationDialogOpen(true))}>
                {t("misc:cancel")} {getLocalizedBookingSyntex(country)}
              </Button>
            </>
          )}
          {[
            BookingStatus.CONFIRMED,
            BookingStatus.IN_PROGRESS,
            BookingStatus.COMPLETED,
          ].includes(booking.status) && (
              <>
                {(booking.status === BookingStatus.QUOTE ||
                  booking.status === BookingStatus.CONFIRMED) && (
                    <Tooltip
                      title={
                        d.fromISO(booking.pickupDateTime) < d.now() || booking.zeroCostBooking
                          ? `${t("root:pickupDateTimePassed")} ${getLocalizedBookingSyntex(country).toLowerCase()}`
                          : ""
                      }
                    >
                      <Grid item>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="large"
                          style={{
                            color: d.fromISO(booking.pickupDateTime) < d.now() || booking.zeroCostBooking
                              ? "rgba(0,0,0,0.3)"
                              : "#D13135",
                            textTransform: "initial",
                            fontSize: 14,
                            fontWeight: 500,
                          }}
                          onClick={() => {
                            d.fromISO(booking.pickupDateTime).toISO() > d.now().toISO() && setCancellationDialogOpen(true)
                          }}
                          disabled={!canCancel() || booking.zeroCostBooking || d.fromISO(booking.pickupDateTime) < d.now()}
                        >
                          {t("misc:cancel")} {getLocalizedBookingSyntex(country)}
                        </Button>
                      </Grid>
                    </Tooltip>
                  )}
                <span className="padding-right"></span>
                {booking.status === BookingStatus.CONFIRMED && <Tooltip
                  title={
                    d.fromISO(booking.pickupDateTime) < d.now() || booking.zeroCostBooking
                      ? `${t("root:pickupDateTimePassed")} ${getLocalizedBookingSyntex(country).toLowerCase()}`
                      :
                      (user?.customer &&
                        website.organisation.requiresCustomerVerification &&
                        (user?.customer?.status === CustomerVerificationStatus.PENDING ||
                          user?.customer?.status === CustomerVerificationStatus.DECLINED) ? t("root:profileRejectedOrPending") : "") ||
                      (user?.businesscustomers &&
                        website.organisation.requiresCustomerVerification &&
                        (user?.businesscustomers?.status === CustomerVerificationStatus.PENDING ||
                          user?.businesscustomers?.status === CustomerVerificationStatus.DECLINED) ? t("root:profileRejectedOrPending") : "")
                  }
                >
                  <span>
                    {
                      !hideUpdateButton && (
                        <Button
                          type="submit"
                          variant={
                            d.fromISO(booking.pickupDateTime) < d.now() ||
                              booking.zeroCostBooking ||
                              (user?.customer &&
                                website.organisation.requiresCustomerVerification &&
                                (user?.customer?.status === CustomerVerificationStatus.PENDING ||
                                  user?.customer?.status === CustomerVerificationStatus.DECLINED)) ||
                              (user?.businesscustomers &&
                                website.organisation.requiresCustomerVerification &&
                                (user?.businesscustomers?.status === CustomerVerificationStatus.PENDING ||
                                  user?.businesscustomers?.status === CustomerVerificationStatus.DECLINED))
                              ? "outlined"
                              : "contained"
                          }
                          color="primary"
                          size="large"
                          disabled={
                            d.fromISO(booking.pickupDateTime) < d.now() ||
                            booking.zeroCostBooking ||
                            (user?.customer &&
                              website.organisation.requiresCustomerVerification &&
                              (user?.customer?.status === CustomerVerificationStatus.PENDING ||
                                user?.customer?.status === CustomerVerificationStatus.DECLINED)) ||
                            (user?.businesscustomers &&
                              website.organisation.requiresCustomerVerification &&
                              (user?.businesscustomers?.status === CustomerVerificationStatus.PENDING ||
                                user?.businesscustomers?.status === CustomerVerificationStatus.DECLINED))
                          }
                          style={{
                            textTransform: "initial",
                            boxShadow: "none",
                            fontSize: 14,
                            fontWeight: 500,
                            color:
                              d.fromISO(booking.pickupDateTime) < d.now() || booking.zeroCostBooking
                                ? "rgba(0,0,0,0.3)"
                                : undefined,
                            border:
                              d.fromISO(booking.pickupDateTime) < d.now() || booking.zeroCostBooking
                                ? "1px solid rgba(0,0,0,0.23)"
                                : undefined,
                            background:
                              d.fromISO(booking.pickupDateTime) < d.now() || booking.zeroCostBooking
                                ? "transparent"
                                : "var(--primary-color)",
                          }}
                          onClick={() => {
                            let bookingType = booking.isSubscription ? BookingType.SUBSCRIPTION : BookingType.RENTAL;
                            if (booking.bookingCategory) {
                              bookingType = booking.bookingCategory;
                            }
                            d.fromISO(booking.pickupDateTime) > d.now() &&
                              history.push(`/update-booking?booking=${booking.id}&bookingType=${bookingType}`);
                          }}
                        >
                          {t("root:update")}
                        </Button>
                      )
                    }

                  </span>
                </Tooltip>}
                <span className="padding-right"></span>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  style={{
                    background: "var(--primary-color)",
                    textTransform: "initial",
                    boxShadow: "none",
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                  onClick={() => {
                    if (rentalAgreementUrl) {
                      setPdfId(rentalAgreementUrl);
                      setPdfViewMode(PDFViewMode.RENTAL_AGGREMENT);
                      setPdfFileName(
                        isContractHire && booking.isSubscription
                          ? `ContractHire-${booking.referenceNumber}.pdf`
                          : booking.isSubscription
                            ? `SubscriptionAgreement-${booking.referenceNumber}.pdf`
                            : booking.bookingCategory === (BOOKING_CATEGORY.TEST_DRIVE as string)
                              ? 'Test Drive Agreement'
                              : booking.isFleetAllocation
                                ? `User-Agreement-${booking.referenceNumber}.pdf`
                                : `${getLocalizedRASyntex(country)}-${booking.referenceNumber}.pdf`
                      );
                      setSendEmail(true);
                      setPdfTitle(`${booking.bookingCategory == (BOOKING_CATEGORY.TEST_DRIVE as string) ? t('root:testDriveAgreement') : t('root:rentalAgreement')}`);
                      setPdfOpen(true);
                    } else {
                      history.push(`./rental-agreement?booking=${booking.id}`);
                    }
                  }}
                  disabled={!rentalAgreementUrl}
                >
                  {isContractHire && booking.isSubscription
                    ? t("root:contractHireAgreement")
                    : booking.isSubscription
                      ? t("root:subscriptionAgreement")
                      : booking.bookingCategory === (BOOKING_CATEGORY.TEST_DRIVE as string)
                        ? t('root:testDriveAgreement')
                        : booking.isFleetAllocation
                          ? t('root:userAgreement')
                          : getLocalizedRASyntex(country)
                  }
                </Button>
                {booking.status === BookingStatus.IN_PROGRESS && website.organisation.enableSelfServiceBookingExtension && (
                  <>
                    <span className="padding-right"></span>
                    <Tooltip
                      title={
                        booking?.latestExtension?.status === "QUOTE"
                          ? t("root:extensionAlreadyPending") || ""
                          : ""
                      }
                    >
                      <span>
                        {/* NU-12537 QA fix: disabling this whenever a prior request was
                            left pending (e.g. unavailable for the originally-requested
                            end date) blocked the customer from ever trying again with a
                            shorter, possibly-available date - the backend
                            (consumerRequestBookingExtension) already supersedes a
                            pending request in place when a new one is made, so the
                            button only needs to stay enabled; the tooltip still informs
                            the customer a request is under review. */}
                        <Button
                          variant="outlined"
                          color="primary"
                          size="large"
                          style={{ textTransform: "initial", fontSize: 14, fontWeight: 500 }}
                          onClick={() => setExtendBookingDialogOpen(true)}
                        >
                          {t("root:extendBooking")}
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
              </>
            )}
          {booking.status === BookingStatus.QUOTE && (
            <>
              {
                booking?.bookingCategory !== BookingType.SUBSCRIPTION && (
                  <DownloadPdf
                    getSource={{ type: "QUOTE", id: booking.id }}
                    pdfSource={booking.quoteUrl}
                    fileName={`${t("root:quote")}-${booking?.referenceNumber}.pdf`}
                  />
                )
              }
              <span className="padding-right"></span>
              {booking.zeroCostBooking ? (
                <Tooltip
                  title={t("root:zeroChargeBooking")}
                >
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={booking.zeroCostBooking}
                      style={{
                        background: "var(--primary-color)",
                        textTransform: "initial",
                        boxShadow: "none",
                        fontSize: 14,
                        fontWeight: 500,
                        color: booking.zeroCostBooking
                          ? "rgba(255,255,255,0.3)"
                          : ""
                      }}
                    >
                      {t("misc:payConfirm")}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip
                  title={
                    d.fromISO(booking.pickupDateTime) < d.now()
                      ? t("root:pickupDateTimeInPast")
                      : ""
                  }
                >
                  {(website.organisation.closeGroupSharingEnabled || isApprovedDriverSkippingPayment) ? (
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={() => {
                        confirmBookingMutation({
                          variables: {
                            bookingId: booking?.id,
                            branchId: booking?.branchId,
                            bookingType: "PREPAID"
                          }
                        });
                      }}
                      style={{
                        background: "var(--primary-color)",
                        textTransform: "initial",
                        boxShadow: "none",
                        fontSize: 14,
                        fontWeight: 500,
                        color: booking.zeroCostBooking
                          ? "rgba(255,255,255,0.3)"
                          : ""
                      }}
                    >
                      {t("root:confirmBooking")}
                    </Button>) : (
                    <Button
                      onClick={() => handlePayAndConfirm()}
                      variant="contained"
                      color="primary"
                      size="large"
                      style={{
                        background: "var(--primary-color)",
                        textTransform: "initial",
                        boxShadow: "none",
                        fontSize: 14,
                        fontWeight: 500,
                        color:
                          d.fromISO(booking.pickupDateTime) < d.now()
                            ? "rgba(255,255,255,0.3)"
                            : "",
                      }}
                    >
                      {t("misc:payConfirm")}
                    </Button>)
                  }

                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>
      {booking.status === BookingStatus.QUOTE ? (
        <Quote
          booking={booking}
          onBookingUpdated={(updated) => setBooking({ ...booking, ...updated })}
        />
      ) : (
        <BookingSummary booking={booking} />
      )}
      {openVerificationDialog && (
        <VerificationDialog
          open={openVerificationDialog}
          handleClose={() => setOpenVerificationDialog(false)}
          user={user}
          require={allRequiredDocument}
        />
      )}
      {openIdentityVerificationDialog && user?.customer && (
        <Dialog
          open={openIdentityVerificationDialog}
          onClose={() => {
            if (!isIdentityVerificationMandatory) {
              setOpenIdentityVerificationDialog(false);
            }
          }}
          aria-labelledby="identity-verification-dialog-title"
        >
          <DialogTitle id="identity-verification-dialog-title" disableTypography>
            <div className="flex cross-center space-between">
              <Typography variant="h2" className="bold">Verify your identity</Typography>
              {!isIdentityVerificationMandatory && (
                <IconButton onClick={() => setOpenIdentityVerificationDialog(false)} aria-label="close">
                  <CloseIcon />
                </IconButton>
              )}
            </div>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {isIdentityVerificationMandatory
                ? "Identity verification is required before you can confirm this booking."
                : "Please verify your identity to continue confirming this booking."}
            </DialogContentText>
            <IdentityVerificationCard
              customer={user.customer}
              onStatusChange={(status) => {
                setIdentityVerificationStatusOverride(status);
                if (status === IdentityVerificationStatus.APPROVED) {
                  setOpenIdentityVerificationDialog(false);
                  snackbar({
                    message: "Identity verification approved.",
                    variant: SnackBarVariant.SUCCESS
                  });
                  handlePayAndConfirm(true);
                } else if (status === IdentityVerificationStatus.IN_REVIEW) {
                  setOpenIdentityVerificationDialog(false);
                  snackbar({
                    message: "Your identity verification is under review.",
                    variant: SnackBarVariant.INFORMATION
                  });
                  handlePayAndConfirm(true);
                }
              }}
            />
            {!isIdentityVerificationMandatory && (
              <Button
                variant="text"
                fullWidth
                onClick={() => {
                  setIdentityVerificationSkipped(true);
                  setOpenIdentityVerificationDialog(false);
                  handlePayAndConfirm(true);
                }}
              >
                Skip for Now
              </Button>
            )}
          </DialogContent>
        </Dialog>
      )}
      <BusinessCustomerVerificationDialog
        open={dialogBoxOpen}
        handleClose={() => setDialogBoxOpen(false)}
      />
      {extendBookingDialogOpen && booking && (
        <ExtendBookingDialog
          open={extendBookingDialogOpen}
          onClose={() => setExtendBookingDialogOpen(false)}
          booking={booking}
          timeZone={booking.branch?.timeZone}
          onExtended={() => getBooking({ variables: { bookingId: booking.id } })}
        />
      )}
    </div>
  );
};

export default BookingManagement;